/**
 * CampaignScheduleDialog — Modal para agendar campanha a partir de uma data.
 * Público (todos/vip/inativos_30d) + template pré-escrito + N dias antes.
 */
import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Users, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const AUDIENCES = [
  { value: "all", label: "Todos os clientes" },
  { value: "active", label: "Somente ativos" },
  { value: "vip", label: "VIPs (500+ pontos)" },
  { value: "inactive_30d", label: "Inativos há 30+ dias" },
];

function suggestBody(holiday) {
  const h = holiday || {};
  const idea = (h.ideas && h.ideas[0]) || "combo especial com desconto";
  return `Oi {{client_name}}! ${h.name || "Data especial"} ${h.emoji || ""} tá chegando — que tal aproveitar ${idea.toLowerCase()}? Responde aqui pra reservar.`.trim();
}

export default function CampaignScheduleDialog({ open, onOpenChange, holiday, onScheduled }) {
  const [form, setForm] = useState({ audience: "all", days_before: 5, body: "" });
  const [audienceCount, setAudienceCount] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open && holiday) {
      setForm({
        audience: "all",
        days_before: Math.min(5, Math.max(1, holiday.days_to || 1)),
        body: suggestBody(holiday),
      });
    }
  }, [open, holiday]);

  useEffect(() => {
    if (!open) return;
    api.get("/tenant/campaigns/audience-estimate", { params: { audience: form.audience } })
      .then(({ data }) => setAudienceCount(data.count))
      .catch(() => setAudienceCount(null));
  }, [open, form.audience]);

  const scheduledDate = (() => {
    if (!holiday?.date) return null;
    const target = new Date(holiday.date + "T09:00:00");
    target.setDate(target.getDate() - Number(form.days_before || 0));
    return target;
  })();

  const submit = async () => {
    if (!scheduledDate) return;
    setBusy(true);
    try {
      await api.post("/tenant/campaigns/schedule", {
        name: `${holiday.name} — ${holiday.date}`,
        body: form.body,
        audience: form.audience,
        scheduled_at: scheduledDate.toISOString(),
        holiday_key: holiday.name,
      });
      toast.success(`Campanha agendada para ${scheduledDate.toLocaleDateString("pt-BR")}`);
      onScheduled?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e, "Erro ao agendar campanha"));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="campaign-schedule-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <span className="text-xl">{holiday?.emoji}</span>
            Criar campanha: {holiday?.name}
          </DialogTitle>
          <DialogDescription>
            Envie mensagens WhatsApp automaticamente antes da data. Personalize o público e o texto.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-xs">Público-alvo</Label>
            <Select value={form.audience} onValueChange={(v) => setForm({ ...form, audience: v })}>
              <SelectTrigger data-testid="campaign-audience"><SelectValue /></SelectTrigger>
              <SelectContent>
                {AUDIENCES.map((a) => (
                  <SelectItem key={a.value} value={a.value} data-testid={`campaign-audience-${a.value}`}>
                    {a.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {audienceCount !== null && (
              <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                <Users className="w-3 h-3" />
                Vai atingir <b data-testid="campaign-audience-count">{audienceCount}</b> cliente{audienceCount !== 1 ? "s" : ""}
              </div>
            )}
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs">Disparar quantos dias antes?</Label>
            <Input
              type="number" min={0} max={Math.max(1, holiday?.days_to || 30)}
              data-testid="campaign-days-before"
              value={form.days_before}
              onChange={(e) => setForm({ ...form, days_before: e.target.value })}
            />
            {scheduledDate && (
              <div className="text-[11px] text-muted-foreground">
                Será enviada em <b>{scheduledDate.toLocaleDateString("pt-BR", { weekday: "long", day: "2-digit", month: "long" })}</b>
              </div>
            )}
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs">Mensagem (WhatsApp)</Label>
            <textarea
              className="w-full text-sm rounded-md border border-border bg-card px-3 py-2 font-mono"
              rows={5}
              data-testid="campaign-body"
              value={form.body}
              onChange={(e) => setForm({ ...form, body: e.target.value })}
            />
            <div className="text-[10px] text-muted-foreground">
              Variáveis: <code>{"{{client_name}}"}</code>, <code>{"{{tenant_name}}"}</code>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="campaign-cancel">Cancelar</Button>
          <Button
            onClick={submit}
            disabled={busy || !form.body}
            data-testid="campaign-submit"
            className="gap-2"
            style={{ background: "hsl(var(--tenant-primary))", color: "hsl(var(--tenant-primary-foreground))" }}
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            Agendar campanha
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
